/**
 * Compteur de référencement (« build your own ») et récap des collections entamées.
 * Blocs de thème K&J : blocks/kj-palier-compteur.liquid, blocks/kj-palier-recap.liquid.
 * Le comptage est fait en Liquid ; ici on ne fait que : re-rendre le bloc à chaque mise à jour du panier
 * (événement natif cart:updated + Section Rendering API), ajouter / retirer des colis (steppers), ajouter le
 * carton offert, et gérer le mode panneau (section collante bornée au contenu du gabarit).
 * Spec : specs/theme/BUILD-YOUR-OWN.md
 */
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/* K&J (29/09/2026, entrée 252) : toute écriture panier passe par la file unique (layout/theme.liquid),
   une requête à la fois, dans l'ordre des clics. Sans la file (page sans le layout) : exécution directe. */
const kjFile = (job) => (window.kjPanierFile ? window.kjPanierFile.run(job) : job());

const DESKTOP = window.matchMedia('(min-width: 990px)');

// Entrée 382 : ligne d'éco-contribution (le compteur de l'en-tête ne la compte pas).
const estEco = (l) => (window.kjEco && window.kjEco.estEco ? window.kjEco.estEco(l) : l.product_type === 'Éco-contribution');

class KjPalierBase extends HTMLElement {
  connectedCallback() {
    this.handler = (e) => this.refresh(e);
    document.addEventListener(ThemeEvents.cartUpdated, this.handler);
    // K&J (09/10, panier rapide) : un rendu jeté parce que périmé est refait dès que la file est vide.
    // Avant : jeté sans rattrapage, la box gardait une recette retirée du tiroir (bug « ne se retire pas »).
    this.calme = () => {
      if (!this.aRefaire) return;
      this.aRefaire = false;
      this.refresh();
    };
    document.addEventListener('kj:panier-calme', this.calme);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.handler);
    document.removeEventListener('kj:panier-calme', this.calme);
  }

  // Re-rend le bloc après une mise à jour du panier. Si la section est déjà dans l'événement (nos propres
  // appels la demandent au panier en même temps), on l'utilise : zéro requête en plus. Sinon, Section
  // Rendering API. Dans le tiroir et la page panier, rien à faire : ils se re-rendent eux-mêmes.
  async refresh(event) {
    if (this.parentElement && this.parentElement.closest('kj-palier-compteur')) return;
    if (this.closest('cart-drawer, cart-component')) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    const sectionId = section.id.replace('shopify-section-', '');
    // Panier rapide (12/10) : d'autres écritures attendent encore dans la file : ce rendu ne montre qu'une partie des clics
    // (3 recettes ajoutées vite : la box retombait 7 → 5 → 6 → 7 au fil des réponses). On garde l'affichage au clic et on
    // rend une seule fois, quand la file est vide (kj:panier-calme).
    if (window.kjPanierFile && window.kjPanierFile.attente > 1) {
      const vivant = document.querySelector(`[data-block-id="${this.dataset.blockId}"]`) || this;
      vivant.aRefaire = true;
      return;
    }
    const deja = event?.detail?.data?.sections?.[sectionId];
    if (deja) {
      this.remplacer(deja);
      return;
    }
    // Les écritures K&J (cartes, tiroir, page panier, compteur) ramènent déjà nos sections : un événement sans
    // notre section (éco-contribution, réassort) ne change rien à la sélection. Pas de requête inutile.
    if (event?.detail?.data?.source === 'kj-eco') return;
    const url = new URL(location.href);
    url.searchParams.set('sections', sectionId);
    const tour = window.kjPanierFile?.tour;
    try {
      // Pas d'en-tête Accept: application/json : Shopify renverrait le JSON de la ressource au lieu de la section.
      const res = await fetch(url.toString());
      const data = await res.json();
      // Une écriture panier s'est terminée pendant la lecture : ce rendu est périmé ; on le refait au calme.
      if (window.kjPanierFile && (window.kjPanierFile.tour !== tour || window.kjPanierFile.attente)) {
        const vivant = document.querySelector(`[data-block-id="${this.dataset.blockId}"]`) || this;
        // Entrée 382 : l'écriture concurrente est déjà finie (son « calme » est passé pendant la lecture) : on relit tout de
        // suite. Avant : on attendait un « calme » qui ne revenait pas, la box restait sur son affichage au clic.
        if (!window.kjPanierFile.attente && (vivant.kjRelances || 0) < 3) {
          vivant.kjRelances = (vivant.kjRelances || 0) + 1;
          vivant.refresh();
          return;
        }
        vivant.aRefaire = true;
        return;
      }
      this.remplacer(data[sectionId] || '');
    } catch (e) {
      console.warn('kj-palier : rafraîchissement impossible', e);
    }
  }

  // Remplace ce bloc par sa version fraîche (HTML de sa section), en gardant les positions de défilement.
  remplacer(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const neuf = doc.querySelector(`[data-block-id="${this.dataset.blockId}"]`);
    if (!neuf) return;
    const liste = this.querySelector('[data-ref="recettes"]');
    const scrolls = { liste: liste ? liste.scrollTop : 0, panneau: this.scrollTop };
    this.replaceWith(neuf);
    const listeNeuve = neuf.querySelector('[data-ref="recettes"]');
    if (listeNeuve) listeNeuve.scrollTop = scrolls.liste;
    neuf.scrollTop = scrolls.panneau;
  }

  // Appel panier : la réponse ramène en même temps la section du tiroir et la nôtre (une seule requête),
  // puis l'événement natif fait suivre le tiroir, l'icône et nos blocs.
  async panier(route, body, delta) {
    const section = this.closest('.shopify-section');
    const propre = section && section.id.replace('shopify-section-', '');
    let data;
    if (window.kjPanierFile?.ecrire) {
      data = await window.kjPanierFile.ecrire(route, body, [propre]);
    } else {
      const sections = [document.querySelector('cart-drawer')?.dataset.sectionId, propre].filter(Boolean);
      const res = await fetch(route, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, sections: sections.join(','), sections_url: location.pathname }),
      });
      data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Erreur panier.');
    }
    // Entrée 382 : panier complet dans la réponse (update.js, change.js) : le compteur de l'en-tête en est tiré (hors éco).
    // Sinon (add.js) : compteur affiché + `delta` (colis pas encore montrés au clic).
    const count = document.querySelector('cart-icon')?.getCurrentItemCount();
    const plein = data && data.token && Array.isArray(data.items);
    const ev = new CartUpdatedEvent({}, {
      itemCount: plein
        ? data.items.reduce((s, l) => s + (estEco(l) ? 0 : l.quantity), 0)
        : count != null && !Number.isNaN(count) ? Math.max(0, count + delta) : undefined,
      source: 'kj-palier',
      sections: data.sections,
    });
    // change.js renvoie le panier complet : les cartes de la grille (quick-add.js) le lisent sans requête.
    if (data.token && data.items) ev.kjPanier = Promise.resolve(data);
    document.dispatchEvent(ev);
    return data;
  }
}

class KjPalierCompteur extends KjPalierBase {
  static ouvert = false;
  static ecouteurs = false;
  static cibles = new Map(); // variante → quantité voulue pendant une rafale (vidée quand la file est vide)
  static envoyes = new Map(); // entrée 382 : variante → dernière quantité envoyée et acceptée pendant la rafale (vidée avec cibles)
  static enFile = new Map(); // entrée 382 : bloc → envois « recettes » de la box en file, pas encore partis
  static retraits = new Set(); // clés de lignes offertes en cours de retrait (entrée 380 bis)
  static aRetirer = new Map(); // entrée 381 : clé → { cible, enleve } des colis offerts à retirer, pas encore envoyés
  static essais = new Map(); // entrée 382 : clé → retraits automatiques déjà demandés sur cette page

  // Entrée 381 : retraits de colis offerts en attente, pris par la prochaine écriture (une seule requête pour tout).
  static prendreRetraits() {
    const updates = {};
    let delta = 0;
    const cles = [];
    KjPalierCompteur.aRetirer.forEach((v, cle) => { updates[cle] = v.cible; delta -= v.enleve; cles.push(cle); });
    KjPalierCompteur.aRetirer.clear();
    return { updates, delta, cles, fin: () => cles.forEach((c) => KjPalierCompteur.retraits.delete(c)) };
  }

  // Entrée 381 : ajoute la correction d'éco-contribution à une mise à jour (même requête), si kj-eco sait la calculer.
  static avecEco(updates) {
    try { return Object.assign(updates, (window.kjEco && window.kjEco.majPour && window.kjEco.majPour(updates)) || {}); } catch (e) { return updates; }
  }

  static preneurs = 0; // entrée 382 : clics de la box en file, pas encore partis (ils emporteront les retraits en attente)

  // Entrée 382 : le panier suit le clic de la box sans attendre la réponse (1 à 2 s client connecté) : compteur de
  // l'en-tête (`delta` colis), lignes du tiroir et de la page panier ({ clé: quantité } : à 0 la ligne disparaît, sinon
  // nouvelle quantité, ligne grisée) et totaux du tiroir grisés. La réponse de l'écriture redessine tout au propre ;
  // en cas d'erreur, « kj:panier-calme » remet les lignes et la synchro d'éco recale le compteur.
  static panierOptimiste(lignes, delta) {
    if (delta) {
      document.querySelectorAll('cart-icon [data-ref="cart-count"]').forEach((c) => {
        const n = parseInt(c.textContent, 10);
        if (!Number.isNaN(n)) c.textContent = Math.max(0, n + delta);
      });
    }
    const cles = Object.keys(lignes || {});
    if (cles.length) {
      document.querySelectorAll('cart-drawer-item[data-key], cart-item[data-key]').forEach((el) => {
        if (!cles.includes(el.dataset.key)) return;
        const qte = parseInt(lignes[el.dataset.key], 10) || 0;
        el.dataset.kjOpt = '1';
        if (qte <= 0) { el.classList.add('kj-retrait'); return; }
        const input = el.querySelector('[data-ref="quantity-selector-input"]');
        if (input) input.value = qte;
        el.classList.add('cart-drawer__item--loading');
      });
    }
    if (delta || cles.length) document.querySelector('cart-drawer')?.classList.add('kj-maj');
  }

  // Entrée 381-382 : envoie les retraits de colis offerts en attente (update.js, éco jointe), dans le job en cours.
  static async envoyerRetraits(bloc) {
    const x = KjPalierCompteur.prendreRetraits();
    if (!x.cles.length) return;
    try {
      const updates = KjPalierCompteur.avecEco({ ...x.updates });
      try {
        await bloc.panier('/cart/update.js', { updates }, x.delta);
      } catch (err) {
        // Une clé d'éco périmée fait refuser toute la requête (422 « Cannot find variant ») : sans l'éco, la synchro suivra.
        if (Object.keys(updates).length === x.cles.length) throw err;
        await bloc.panier('/cart/update.js', { updates: { ...x.updates } }, x.delta);
      }
    } finally {
      x.fin();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.highlight();
    const form = this.querySelector('[data-ref="offert-form"]');
    if (form) form.addEventListener('submit', (e) => this.ajouterOffert(e, form));
    this.addEventListener('click', (e) => this.stepper(e));
    this.reappliquerCibles();
    // Entrée 380 bis : rendu serveur au calme avec plus de colis offerts que de droits (recette retirée ailleurs) : on corrige.
    if (!(window.kjPanierFile && window.kjPanierFile.attente)) this.offertOptimiste(parseInt(this.dataset.nb, 10) || 0, true);
    // K&J (09/10, panier rapide) : ajout depuis une carte de la grille : compteur, jauge et liseré bougent au clic.
    this.surCarte = (e) => this.optimisteCarte(e.detail);
    document.addEventListener('kj:optimiste', this.surCarte);
    // Panier rapide (09/10) : retrait / quantité changés dans le tiroir ou la page panier : la box suit au clic.
    this.surLigne = (e) => this.optimisteLigne(e.detail);
    document.addEventListener('kj:optimiste-ligne', this.surLigne);
    if (this.dataset.disposition === 'panneau') this.panneau();
    this.marquer();
  }

  // Repère sur la grille : chaque carte produit dont la variante est au panier (data-selection = « id:qté, »,
  // rendu serveur, donc à jour à chaque re-rendu du bloc) reçoit la classe kj-selection (liseré autour de
  // l'image, CSS du bloc). Les autres cartes sont nettoyées.
  marquer() {
    this.marquerDeja();
    if (this.dataset.selection == null) return;
    const map = new Map();
    this.dataset.selection.split(',').forEach((paire) => {
      const [id, qty] = paire.split(':');
      if (id) map.set(id, parseInt(qty, 10) || 1);
    });
    if (this.dataset.selectionCouleur) {
      document.documentElement.style.setProperty('--kj-selection-couleur', this.dataset.selectionCouleur);
    }
    document.querySelectorAll('product-card').forEach((card) => {
      const id =
        card.querySelector('[data-ref="add-to-cart-button"][data-variant-id]')?.dataset.variantId ||
        card.querySelector('input[name="id"]')?.value;
      KjPalierCompteur.marquerCarte(card, id ? map.get(String(id)) : undefined);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('kj:optimiste', this.surCarte);
    document.removeEventListener('kj:optimiste-ligne', this.surLigne);
  }

  // Carte de la grille : { variantId, avant, apres } (quantités payantes affichées). Seules les recettes du groupe
  // de ce compteur comptent : on le sait par une carte de la grille de ce couloir ou par la liste déjà rendue.
  optimisteCarte(d) {
    if (!d || !d.variantId) return;
    const id = String(d.variantId);
    if (!this.dataset.groupeIds || !this.dataset.groupeIds.split(',').includes(id)) return;
    document.querySelectorAll('product-card').forEach((card) => {
      const v = card.querySelector('[data-ref="add-to-cart-button"][data-variant-id]')?.dataset.variantId;
      if (v && String(v) === id) KjPalierCompteur.marquerCarte(card, d.apres);
    });
    const li = this.querySelector(`li[data-variant-id="${id}"]:not(.kj-palier__recette--offert)`);
    if (li) {
      const q = li.querySelector('[data-ref="qty"]');
      if (q) q.textContent = d.apres;
      li.dataset.qtyAffichee = d.apres;
      li.classList.toggle('kj-retrait', d.apres === 0);
    } else if (d.apres > 0) {
      this.ligneProvisoire(id, d);
    }
    // Recette déjà dans le rayon (historique) : elle compte déjà, le nombre ne bouge pas.
    if (this.dataset.deja && this.dataset.deja.split(',').includes(id)) return;
    let delta = 0;
    if (d.avant === 0 && d.apres > 0) delta = 1;
    if (d.avant > 0 && d.apres === 0) delta = -1;
    if (delta) this.ajusterNb(delta);
  }

  // Panier rapide (09/10) : nouvelle recette ajoutée depuis une carte : sa ligne entre dans « Ma sélection » au clic
  // (photo et nom repris de la carte), en tête comme dans le panier. Boutons inactifs jusqu'au rendu serveur, qui la
  // remplace par la vraie ligne (clé du panier, quantité confirmée).
  // Ligne du panier visée par sa clé (tiroir, page panier) : { key, quantity }.
  optimisteLigne(d) {
    if (!d || !d.key) return;
    const li = [...this.querySelectorAll('li[data-line-key]')].find((x) => x.dataset.lineKey === d.key);
    if (!li) return;
    const id = String(li.dataset.variantId);
    const apres = Math.max(0, parseInt(d.quantity, 10) || 0);
    if (li.classList.contains('kj-palier__recette--offert')) {
      if (apres === 0) li.classList.add('kj-retrait');
      return;
    }
    const avant = parseInt(li.dataset.qtyAffichee ?? li.dataset.qty, 10) || 0;
    const q = li.querySelector('[data-ref="qty"]');
    if (q) q.textContent = apres;
    li.dataset.qtyAffichee = apres;
    li.classList.toggle('kj-retrait', apres === 0);
    document.querySelectorAll('product-card').forEach((card) => {
      const v = card.querySelector('[data-ref="add-to-cart-button"][data-variant-id]')?.dataset.variantId;
      if (v && String(v) === id) KjPalierCompteur.marquerCarte(card, apres);
    });
    if (this.dataset.deja && this.dataset.deja.split(',').includes(id)) return;
    if (avant > 0 && apres === 0) this.ajusterNb(-1);
    if (avant === 0 && apres > 0) this.ajusterNb(1);
  }

  ligneProvisoire(id, d) {
    const carte = d.carte || [...document.querySelectorAll('product-card')].find(
      (c) => c.querySelector('[data-ref="add-to-cart-button"]')?.dataset.variantId === id,
    );
    const zone = this.querySelector('.kj-palier__recettes');
    if (!carte || !zone) return;
    const img = carte.querySelector('img');
    const nom = [...carte.querySelectorAll('a[href*="/products/"]')].map((a) => a.textContent.trim()).find(Boolean) || img?.alt || '';
    if (!nom) return;
    let ul = zone.querySelector('[data-ref="recettes"]');
    if (!ul) {
      ul = document.createElement('ul');
      ul.className = 'kj-palier__grille';
      ul.dataset.ref = 'recettes';
      zone.querySelector('.kj-palier__recettes-vide')?.remove();
      zone.appendChild(ul);
    }
    const li = document.createElement('li');
    li.className = 'kj-palier__recette is-in-cart kj-attente';
    li.dataset.variantId = id;
    li.dataset.qty = '0';
    li.dataset.qtyAffichee = String(d.apres);
    if (img) {
      const i = document.createElement('img');
      i.className = 'kj-palier__recette-img';
      i.src = img.currentSrc || img.src;
      i.alt = '';
      i.width = 60;
      i.height = 60;
      li.appendChild(i);
    }
    const n = document.createElement('span');
    n.className = 'kj-palier__recette-nom';
    n.title = nom;
    n.textContent = nom;
    li.appendChild(n);
    const st = document.createElement('span');
    st.className = 'kj-palier__stepper';
    st.innerHTML = '<button type="button" disabled aria-label="Retirer un colis">&minus;</button><span class="kj-palier__qty" data-ref="qty"></span><button type="button" disabled aria-label="Ajouter un colis">+</button>';
    st.querySelector('[data-ref="qty"]').textContent = d.apres;
    li.appendChild(st);
    ul.prepend(li);
  }

  static marquerCarte(card, qty) {
    card.classList.toggle('kj-selection', !!qty);
  }

  // Recettes déjà dans le rayon du client (registre, historique compris : data-deja = « id, », rendu serveur) :
  // une ligne « Déjà dans votre rayon » sous le colisage de la carte (gabarit <template data-ref="deja-tpl">).
  // Idempotent : la ligne posée par un rendu précédent est retirée avant.
  marquerDeja() {
    if (this.dataset.deja == null) return;
    const tpl = this.querySelector('template[data-ref="deja-tpl"]');
    const ids = new Set(this.dataset.deja.split(',').filter(Boolean));
    document.querySelectorAll('product-card').forEach((card) => {
      card.querySelectorAll('.kj-deja-carte').forEach((el) => el.remove());
      if (!tpl || !ids.size) return;
      const id =
        card.querySelector('[data-ref="add-to-cart-button"][data-variant-id]')?.dataset.variantId ||
        card.querySelector('input[name="id"]')?.value;
      if (!id || !ids.has(String(id))) return;
      const cible = card.querySelector('.product-card-group') || card;
      cible.appendChild(tpl.content.firstElementChild.cloneNode(true));
    });
  }

  // ?palier=22 (cartes des hubs) : met le jalon visé en avant, rien d'autre.
  highlight() {
    const voulu = new URLSearchParams(location.search).get('palier');
    if (!voulu) return;
    this.querySelectorAll('.kj-palier__segment').forEach((s) => {
      const label = s.querySelector('.kj-palier__jalon');
      if (label && label.textContent.trim().startsWith(voulu + ' ')) s.classList.add('kj-palier__segment--vise');
    });
  }

  // Steppers de la liste des recettes : + ajoute un colis, − en retire un (ligne payante, jamais la ligne offerte).
  // Affichage optimiste : le chiffre, le compteur et la jauge bougent tout de suite ; le rendu serveur confirme.
  // Panier rapide (09/10) : une rafale de clics = une requête. Chaque clic fixe la quantité VOULUE de la ligne
  // (KjPalierCompteur.cibles, survit aux re-rendus du bloc) ; la requête qui part lit la dernière cible et envoie
  // la quantité absolue ; les suivantes, déjà servies, ne partent pas. Avant : un add.js de +1 par clic, en série.
  async stepper(e) {
    const btn = e.target.closest('[data-ref="qty-plus"], [data-ref="qty-minus"]');
    if (!btn) return;
    const li0 = btn.closest('[data-variant-id]');
    if (!li0) return;
    const variant = li0.dataset.variantId;
    const plus = btn.dataset.ref === 'qty-plus';
    const offert = li0.classList.contains('kj-palier__recette--offert');
    const cibles = KjPalierCompteur.cibles;
    // Affichage : cible en attente, sinon vérité (data-qty, rendue par le serveur à chaque réponse).
    const affichee = !offert && cibles.has(variant) ? cibles.get(variant) : parseInt(li0.dataset.qtyAffichee ?? li0.dataset.qty, 10) || 0;
    if (!plus && affichee === 0) return;
    const nouvelle = plus ? affichee + 1 : affichee - 1;
    if (!offert) cibles.set(variant, nouvelle);
    this.optimiste(li0, nouvelle, affichee);
    const blockId = this.dataset.blockId;
    const moi = this;
    // Entrée 382 : recette déjà au panier (ligne avec clé) et un envoi de cette box attend déjà son tour dans la file :
    // il emportera ce clic (il envoie toutes les recettes cliquées de la box). Pas de 2e job : la réponse de cet envoi
    // redessine la box tout de suite (avant : jetée comme partielle, puis la box relue une fois de plus).
    const avecCle = !offert && !!li0.dataset.lineKey;
    const enFile = KjPalierCompteur.enFile;
    if (avecCle && (enFile.get(blockId) || 0) > 0) return;
    if (avecCle) enFile.set(blockId, (enFile.get(blockId) || 0) + 1);
    KjPalierCompteur.preneurs++;
    try {
      await kjFile(async () => {
        KjPalierCompteur.preneurs--;
        if (avecCle) enFile.set(blockId, Math.max(0, (enFile.get(blockId) || 0) - 1));
        // Le bloc a pu être re-rendu entre le clic et l'envoi : on relit la ligne dans le bloc vivant.
        const bloc = (blockId && document.querySelector(`kj-palier-compteur[data-block-id="${blockId}"]`)) || moi;
        const route = window.Theme?.routes?.cart_add_url || '/cart/add.js';
        if (offert) {
          // Ligne du colis offert : « + » ajoute un colis payant de la recette (comportement d'origine) ;
          // « − » retire le colis offert (entrée 380 bis : avant, rien ne partait au panier).
          if (plus) await bloc.panier(route, { items: [{ id: parseInt(variant, 10), quantity: 1 }] }, 1);
          else {
            const lo = bloc.querySelector(`li.kj-palier__recette--offert[data-variant-id="${variant}"]`);
            const cleO = lo?.dataset.lineKey;
            const qO = parseInt(lo?.dataset.qty, 10) || 1;
            if (cleO) await bloc.panier('/cart/change.js', { id: cleO, quantity: qO - 1 }, -1);
          }
          await KjPalierCompteur.envoyerRetraits(bloc);
          return;
        }
        // Entrée 382 : ce clic n'écrit rien (rafale déjà servie) : les retraits de colis offerts en attente partent quand même.
        if (!cibles.has(variant)) return KjPalierCompteur.envoyerRetraits(bloc);
        const voulu = cibles.get(variant);
        const li = bloc.querySelector(`li[data-variant-id="${variant}"]:not(.kj-palier__recette--offert)`);
        const cle = li?.dataset.lineKey;
        const q = parseInt(li?.dataset.qty, 10) || 0;
        const envoyes = KjPalierCompteur.envoyes;
        if (cle) {
          // Entrée 381 : UNE requête (update.js) pour la recette, les colis offerts à retirer et l'éco-contribution.
          // Avant : 3 écritures à la suite (offert, recette, puis éco 900 ms plus tard), 4 à 5 s pour un « − ».
          // Entrée 382 : et pour TOUTES les recettes de la box cliquées depuis le dernier envoi (rafale sur plusieurs
          // recettes : 2 requêtes au plus, au lieu d'une par recette, chacune derrière l'autre).
          const lignes = {};
          const servis = [];
          cibles.forEach((v, id) => {
            if (envoyes.get(id) === v) return; // déjà envoyé dans cette rafale
            const l = bloc.querySelector(`li[data-variant-id="${id}"]:not(.kj-palier__recette--offert)`);
            const k = l?.dataset.lineKey;
            if (!k) return; // ligne posée depuis une carte (pas encore de clé) : son propre envoi l'ajoute
            if (v === (parseInt(l.dataset.qty, 10) || 0) && !envoyes.has(id)) return; // déjà la quantité du panier
            lignes[k] = v;
            servis.push([id, v]);
          });
          if (!servis.length) return KjPalierCompteur.envoyerRetraits(bloc);
          const x = KjPalierCompteur.prendreRetraits();
          try {
            const updates = KjPalierCompteur.avecEco({ ...x.updates, ...lignes });
            // Entrée 382 : la ligne d'éco du tiroir suit tout de suite (recettes et colis offerts : déjà faits au clic).
            KjPalierCompteur.panierOptimiste(updates, 0);
            try {
              await bloc.panier('/cart/update.js', { updates }, 0);
              servis.forEach(([id, v]) => envoyes.set(id, v));
            } catch (err) {
              if (Object.keys(updates).length === servis.length) throw err;
              // Repli : les recettes seules (une clé périmée de colis offert ou d'éco ne doit pas bloquer le clic du client),
              // le reste suivra.
              await bloc.panier('/cart/update.js', { updates: { ...lignes } }, 0);
              servis.forEach(([id, v]) => envoyes.set(id, v));
              x.cles.forEach((c) => KjPalierCompteur.retraits.delete(c));
              // Colis offerts : redemandés par la box VIVANTE (celle d'avant la réponse est déjà remplacée) au calme de la file.
            }
          } finally {
            x.fin();
          }
          return;
        }
        // Recette posée au clic depuis une carte, pas encore de ligne au panier : ajout (add.js).
        if (voulu === q || envoyes.get(variant) === voulu) return KjPalierCompteur.envoyerRetraits(bloc);
        const x = KjPalierCompteur.prendreRetraits();
        try {
          if (x.cles.length) await bloc.panier('/cart/update.js', { updates: KjPalierCompteur.avecEco({ ...x.updates }) }, x.delta);
          // Entrée 382 : delta 0, le compteur de l'en-tête a déjà bougé au clic (panierOptimiste).
          if (voulu > q) {
            await bloc.panier(route, { items: [{ id: parseInt(variant, 10), quantity: voulu - q }] }, 0);
            envoyes.set(variant, voulu);
          }
        } finally {
          x.fin();
        }
      });
    } catch (err) {
      cibles.delete(variant);
      window.kjPanierFile?.signaler();
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: err.message } }));
      if (!window.kjPanierFile) this.refresh();
    }
  }

  // Après un re-rendu pendant une rafale : on remet les quantités voulues à l'écran (pas de retour en arrière).
  reappliquerCibles() {
    if (!KjPalierCompteur.cibles.size) return;
    KjPalierCompteur.cibles.forEach((v, id) => {
      const li = this.querySelector(`li[data-variant-id="${id}"]:not(.kj-palier__recette--offert)`);
      if (!li) return;
      const q = li.querySelector('[data-ref="qty"]');
      if (q) q.textContent = v;
      li.dataset.qtyAffichee = v;
      li.classList.toggle('kj-retrait', v === 0);
    });
  }

  optimiste(li, nouvelle, ancienne) {
    const q = li.querySelector('[data-ref="qty"]');
    if (q) q.textContent = nouvelle;
    li.dataset.qtyAffichee = nouvelle;
    const offert = li.classList.contains('kj-palier__recette--offert');
    // Entrée 382 : recette ramenée à 0 : sa ligne quitte la box au clic, comme son colis offert (avant : « 0 » jusqu'au rendu serveur).
    if (!offert) li.classList.toggle('kj-retrait', nouvelle === 0);
    if (offert) {
      // Colis offert retiré au clic : la ligne s'efface et le choix du colis offert se rouvre tout de suite (entrée 380 bis).
      if (nouvelle < ancienne) {
        li.classList.add('kj-retrait');
        this.dataset.offertsPanier = Math.max(0, (parseInt(this.dataset.offertsPanier, 10) || 0) - (ancienne - nouvelle));
        // Entrée 382 : le tiroir et le compteur de l'en-tête suivent au clic.
        KjPalierCompteur.panierOptimiste(li.dataset.lineKey ? { [li.dataset.lineKey]: nouvelle } : {}, nouvelle - ancienne);
        this.offertOptimiste(parseInt(this.dataset.nb, 10) || 0);
      }
      return;
    }
    // Entrée 382 : le tiroir et le compteur de l'en-tête suivent au clic (le rendu serveur confirmera).
    KjPalierCompteur.panierOptimiste(li.dataset.lineKey ? { [li.dataset.lineKey]: nouvelle } : {}, nouvelle - ancienne);
    // La carte de la grille suit aussi tout de suite (le rendu serveur confirmera).
    if (this.dataset.selection != null) {
      document.querySelectorAll('product-card').forEach((card) => {
        const id = card.querySelector('[data-ref="add-to-cart-button"][data-variant-id]')?.dataset.variantId;
        if (id && String(id) === String(li.dataset.variantId)) {
          KjPalierCompteur.marquerCarte(card, nouvelle);
        }
      });
    }
    // Une recette entre ou sort de la sélection : le compteur et la jauge suivent sans attendre
    // (sauf recette déjà dans le rayon : elle reste comptée par l'historique).
    if (this.dataset.deja && this.dataset.deja.split(',').includes(String(li.dataset.variantId))) return;
    let delta = 0;
    if (ancienne === 0 && nouvelle > 0) delta = 1;
    if (ancienne > 0 && nouvelle === 0) delta = -1;
    if (delta) this.ajusterNb(delta);
  }

  // Compteur « N recettes sur 12 » et première jauge, sans attendre le serveur (le rendu serveur confirmera).
  ajusterNb(delta) {
    const nbEl = this.querySelector('.kj-palier__nb');
    const nb = Math.max(0, (parseInt(this.dataset.nb, 10) || 0) + delta);
    this.dataset.nb = nb;
    const cible = parseInt(this.dataset.cible, 10) || 0;
    const affiche = cible > 0 ? Math.min(nb, cible) : nb;
    if (nbEl) nbEl.textContent = affiche;
    const surEl = this.querySelector('.kj-palier__sur');
    if (surEl?.dataset.un) surEl.textContent = affiche > 1 ? surEl.dataset.plusieurs : surEl.dataset.un;
    const p1 = this.querySelector('.kj-palier__segment[data-jalon="1"] progress');
    if (p1 && cible > 0) p1.value = Math.min(100, (nb * 100) / Math.max(1, parseInt(p1.dataset.seuil || cible, 10)));
    this.offertOptimiste(nb);
  }

  // Entrée 380 : palier atteint (ou perdu) au clic : le choix du colis offert s'ouvre (ou se ferme) tout de suite,
  // avec le même calcul que le Liquid (droits du palier moins offerts déjà obtenus, moins offerts déjà au panier).
  // Avant : il n'apparaissait qu'au rendu serveur, après l'ajout, l'éco-contribution et la relecture (2 à 3 s).
  offertOptimiste(nb, auChargement = false) {
    if (this.dataset.j1r == null) return;
    const n = (k) => parseInt(this.dataset[k], 10) || 0;
    let dus = 0;
    if (n('j1r') > 0 && nb >= n('j1r')) dus = n('j1o');
    if (n('j2r') > 0 && nb >= n('j2r')) dus = n('j2o');
    const droits = Math.max(0, dus - n('dejaOfferts'));
    // Entrée 380 bis : sous le palier, les colis offerts en trop quittent le panier (ligne effacée au clic, retrait en file).
    if (n('offertsPanier') > droits) this.retirerOffertsEnTrop(n('offertsPanier') - droits);
    const form = this.querySelector('[data-ref="offert-form"]');
    if (!form || auChargement) return;
    const restants = Math.max(0, droits - n('offertsPanier'));
    const avant = parseInt(form.dataset.restants, 10) || 0;
    form.dataset.restants = restants;
    form.hidden = restants === 0;
    if (restants > 0 && restants !== avant) {
      const msg = this.getAttribute(`data-msg-atteint-${Math.min(restants, 4)}`);
      const p = this.querySelector('.kj-palier__message');
      if (msg && p) p.textContent = msg;
      const btn = form.querySelector('[data-ref="offert-submit"]');
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    }
  }

  // Mode panneau. Ordinateur : la section du bloc devient collante (hauteur 0, top sous le header), le bloc est
  // absolu à droite dedans ; il démarre à sa place dans la page, suit le défilement, et s'arrête au bas du
  // contenu du gabarit (jamais dans le pied de page). Mobile : barre basse dépliable.
  panneau() {
    if (KjPalierCompteur.ouvert) this.classList.add('is-open');
    const toggle = this.querySelector('[data-ref="toggle"]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(this.classList.contains('is-open')));
      toggle.addEventListener('click', () => {
        KjPalierCompteur.ouvert = this.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(KjPalierCompteur.ouvert));
      });
    }
    this.caler();
    if (!KjPalierCompteur.ecouteurs) {
      KjPalierCompteur.ecouteurs = true;
      const tous = () => document.querySelectorAll('kj-palier-compteur[data-disposition="panneau"]').forEach((el) => el.caler());
      window.addEventListener('resize', tous);
      let tick = false;
      window.addEventListener('scroll', () => {
        if (tick) return;
        tick = true;
        requestAnimationFrame(() => { tous(); tick = false; });
      }, { passive: true });
    }
  }

  caler() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    const body = document.body;
    const gabarit = [...document.querySelectorAll('[id^="shopify-section-template--"]')];
    const i = gabarit.indexOf(section);

    if (!DESKTOP.matches) {
      // Mobile : rien de collant côté section, barre basse fixe (CSS) ; la page garde de la place en bas.
      body.classList.add('kj-panneau-actif');
      section.style.position = '';
      section.style.top = '';
      section.style.height = '';
      section.style.zIndex = '';
      section.style.overflow = '';
      this.style.transform = '';
      gabarit.forEach((s) => s.classList.remove('kj-panneau-voisin'));
      return;
    }
    body.classList.remove('kj-panneau-actif');

    // Largeur du panneau partagée avec les sections voisines (réserve de place, colonnes des grilles).
    const largeur = getComputedStyle(this).getPropertyValue('--panneau-largeur').trim() || '320px';
    document.documentElement.style.setProperty('--kj-panneau-largeur', largeur);

    // 1. Sous quoi le panneau se cale : bas du header, plus toute section collante déjà collée dessous
    //    (barre de navigation des pages À l'unité), hors la nôtre.
    let top = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((s) => {
      const r = s.getBoundingClientRect();
      if (r.bottom > top) top = r.bottom;
    });
    gabarit.forEach((s) => {
      if (s === section) return;
      const pos = getComputedStyle(s).position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      const r = s.getBoundingClientRect();
      if (r.top <= top + 1 && r.bottom > top) top = r.bottom;
    });
    top = Math.round(top) + 16;
    this.style.setProperty('--panneau-top', `${top}px`);

    // 2. Le couloir du panneau = sa section + les grilles de produits qui la suivent immédiatement.
    const estGrille = (s) => !!s.querySelector('.kj-alacarte__grid, .main-collection__products-grid, .collection-featured__grid');
    const voisins = [section];
    for (let j = i + 1; j < gabarit.length && estGrille(gabarit[j]); j++) voisins.push(gabarit[j]);
    gabarit.forEach((s) => s.classList.toggle('kj-panneau-voisin', voisins.includes(s)));

    // 3. Départ au pixel : le haut du panneau s'aligne sur le titre de la première grille (sinon sur sa
    //    première carte). La section porteuse (hauteur 0) précède la grille : le décalage se mesure entre
    //    le titre et le haut de la grille, deux éléments statiques qui défilent ensemble.
    let decalage = 0;
    const premiere = voisins[1];
    if (premiere) {
      const repere = premiere.querySelector('.kj-alacarte__phasehead :is(h1,h2,h3,h4,h5,h6), .main-collection__products, .collection-featured__grid, .product-card');
      if (repere) decalage = Math.max(0, Math.round(repere.getBoundingClientRect().top - premiere.getBoundingClientRect().top));
    }
    this.style.setProperty('--panneau-decalage', `${decalage}px`);

    // 4. La section porteuse devient collante, sans hauteur (elle ne pousse rien). Son « top » est réduit du
    //    décalage pour que le panneau, lui, se colle exactement sous le header.
    section.style.position = 'sticky';
    section.style.top = `${top - decalage}px`;
    section.style.height = '0';
    section.style.zIndex = '1'; // sous le header et la barre de navigation collante (z 2) : en fin de couloir, le panneau glisse dessous
    section.style.overflow = 'visible';

    // 5. Butée basse : le panneau reste TOUJOURS entier et collé sous le header ; quand la place jusqu'au bas de la
    //    dernière carte (ligne du dernier « Ajouter ») devient plus courte que lui, il se raccourcit (défilement
    //    interne, seulement quand c'est nécessaire) ; quand il ne reste plus de place utile (< 160 px), il s'efface
    //    jusqu'à ce qu'on remonte. Il ne glisse jamais sous le header et ne déborde jamais sous la grille.
    this.style.transform = '';
    this.style.maxHeight = '';
    this.classList.remove('is-hors-couloir');
    const fin = voisins[voisins.length - 1];
    if (fin !== section) {
      const cartes = fin.querySelectorAll('.product-card');
      const butee = cartes.length ? cartes[cartes.length - 1] : fin;
      const limite = butee.getBoundingClientRect().bottom;
      const r = this.getBoundingClientRect();
      const disponible = Math.round(limite - r.top);
      if (disponible < 160) {
        this.classList.add('is-hors-couloir');
      } else if (disponible < r.height) {
        this.style.maxHeight = `${disponible}px`;
      }
    }
  }

  // Entrée 380 bis : retire du panier `exces` colis offerts de ce groupe (lignes _offert de la liste du bloc).
  // Chaque ligne n'est retirée qu'une fois, même si plusieurs clics ou re-rendus le redemandent.
  retirerOffertsEnTrop(exces) {
    const blockId = this.dataset.blockId;
    const moi = this;
    let reste = exces;
    const lignes = [...this.querySelectorAll('li.kj-palier__recette--offert[data-line-key]')];
    for (const li of lignes) {
      if (reste <= 0) break;
      const cle = li.dataset.lineKey;
      const q = parseInt(li.dataset.qty, 10) || 1;
      const enlever = Math.min(q, reste);
      reste -= enlever;
      li.classList.add('kj-retrait');
      if (KjPalierCompteur.retraits.has(cle)) continue;
      // Entrée 382 : garde-fou, 3 retraits automatiques au plus par ligne et par page (jamais de boucle d'écritures).
      const essais = KjPalierCompteur.essais.get(cle) || 0;
      if (essais >= 3) continue;
      KjPalierCompteur.essais.set(cle, essais + 1);
      KjPalierCompteur.retraits.add(cle);
      KjPalierCompteur.aRetirer.set(cle, { cible: q - enlever, enleve: enlever });
      // Entrée 382 : la ligne du tiroir et le compteur de l'en-tête suivent au clic.
      KjPalierCompteur.panierOptimiste({ [cle]: q - enlever }, -enlever);
    }
    this.dataset.offertsPanier = Math.max(0, (parseInt(this.dataset.offertsPanier, 10) || 0) - (exces - reste));
    // Entrée 381 : envoi au tour suivant ; si un clic du client part avant (stepper), il emporte ces retraits dans
    // sa propre requête et ce job ne fait plus rien.
    // Entrée 382 : et dans ce cas on ne met plus de job vide dans la file. Avant : la file comptait 2 écritures, la
    // réponse du clic (qui ramène la box) était jetée comme partielle et la box relue une 2e fois (une requête de plus).
    setTimeout(() => {
      if (!KjPalierCompteur.aRetirer.size || KjPalierCompteur.preneurs > 0) return;
      kjFile(async () => {
        const bloc = (blockId && document.querySelector(`kj-palier-compteur[data-block-id="${blockId}"]`)) || moi;
        await KjPalierCompteur.envoyerRetraits(bloc);
      }).catch((err) => {
        window.kjPanierFile?.signaler();
        console.warn('kj-palier : retrait du colis offert impossible', err);
      });
    }, 0);
  }

  // Carton offert : ajout au panier avec la propriété de ligne _offert (lue par le moteur SupaEasy).
  async ajouterOffert(e, form) {
    e.preventDefault();
    const select = form.querySelector('[data-ref="offert-select"]');
    const btn = form.querySelector('[data-ref="offert-submit"]');
    const status = form.querySelector('[data-ref="offert-status"]');
    if (!select || !select.value) return;
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    const variant = parseInt(select.value, 10);
    try {
      await kjFile(() => this.panier(window.Theme?.routes?.cart_add_url || '/cart/add.js', { items: [{ id: variant, quantity: 1, properties: { _offert: '1', _gift_badge: form.dataset.badge || 'Offert' } }] }, 1));
    } catch (err) {
      window.kjPanierFile?.signaler();
      if (status) status.textContent = err.message;
      if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
    }
  }
}

class KjPalierRecap extends KjPalierBase {
  // Disposition repliable : le tiroir et la page panier se re-rendent à chaque modification du panier ;
  // on garde l'état ouvert / fermé choisi par le client pendant la session.
  connectedCallback() {
    super.connectedCallback();
    const plier = this.querySelector('[data-kj-recap-plier]');
    if (!plier) return;
    try {
      if (sessionStorage.getItem('kj-recap-ouvert') === '1') plier.open = true;
    } catch (e) {}
    plier.addEventListener('toggle', () => {
      try {
        sessionStorage.setItem('kj-recap-ouvert', plier.open ? '1' : '0');
      } catch (e) {}
    });
  }
}

if (!customElements.get('kj-palier-compteur')) customElements.define('kj-palier-compteur', KjPalierCompteur);
// Rafale terminée : le serveur fait foi, plus aucune cible en attente.
document.addEventListener('kj:panier-calme', (e) => {
  KjPalierCompteur.cibles.clear();
  KjPalierCompteur.envoyes.clear();
  KjPalierCompteur.enFile.clear();
  // Entrée 382 : file vide sans erreur : une box rendue pendant une écriture (correction sautée au chargement) ou un envoi
  // replié sans ses colis offerts : plus de colis offerts que de droits, on les retire (voir connectedCallback).
  if (!(e.detail && e.detail.erreur)) {
    document.querySelectorAll('kj-palier-compteur').forEach((el) => el.offertOptimiste(parseInt(el.dataset.nb, 10) || 0, true));
  }
  // Entrée 382 : fin des écritures : les totaux du tiroir ne sont plus grisés. Erreur : les lignes masquées ou grisées
  // au clic reviennent et la synchro d'éco relit le panier (compteur de l'en-tête recalé).
  document.querySelector('cart-drawer')?.classList.remove('kj-maj');
  if (e.detail && e.detail.erreur) {
    document.querySelectorAll('[data-kj-opt]').forEach((el) => {
      el.classList.remove('kj-retrait', 'cart-drawer__item--loading');
      delete el.dataset.kjOpt;
    });
    if (window.kjEco && window.kjEco.synchroniser) window.kjEco.synchroniser().catch(() => {});
  }
});
if (!customElements.get('kj-palier-recap')) customElements.define('kj-palier-recap', KjPalierRecap);
